// Hlavní řádek headeru — prvky, které nativní DOM nemá (screens/01-header.md §3,
// m01-header.md §2): ikona lupy v search poli, kontaktní blok (poradkyně se stavem
// podpory, po najetí panel s pracovní dobou a e-mailem), ikony telefon/oblíbené/účet
// a červené kolečko v košíkové pilulce.
// Restyle zbytku (logo, search, košík) řeší CSS. Idempotentní.

import { icon } from './icon.js';
import { fillAvatar } from './teamPhoto.js';
import { shoptetData } from '../shoptet-hooks.js';
import { accountLabel, wholesaleCustomer } from './wholesalePrice.js';
import { HOURS, VO } from '../locale/cs/common.js';
import L from '../locale/cs/headerTools.js';
import SHOP from '../../config/shop.js';
import { page } from './pages.js';
import { asChatTrigger, chatOn } from './supportChat.js';
import { isHoliday } from './dispatchEta.js';
import LC from '../locale/cs/supportChat.js';

// Kontakt a pracovní doba (= handoff §1) z manifestu shopu `contact` — hodnoty,
// obě doby (prodejna × zákaznická podpora) a jejich pravidla jsou popsané tam
// (src/config/bs.config.<shop>.mjs). Moduly čtou kontakt výhradně odsud.
export const CONTACT = {
  email: SHOP.contact.email,
  phone: SHOP.contact.phone,
  // Textový zápis pracovní doby PODPORY („Po–Čt 8:00–15:00, Pá 7:30–14:30") pro kontaktní
  // bloky. NEPÍŠE se ručně — skládá ho `hoursText()` z `supportHours` níž.
  hours: '',
  // Textový zápis otevírací doby PRODEJNY („Po–Pá 7:30–17:00 · So–Ne 8:30–13:00"),
  // taky poskládaný z rozpisu (do 2026-08-31 tu stálo natvrdo jen Po–Pá
  // a sobota chyběla v hlavičce, patičce, na 404 i v klientské sekci).
  storeHours: '',
  // provozní doba prodejny (= výdejního místa) per den v týdnu (0 = neděle)
  openHours: SHOP.contact.openHours,
  // pracovní doba zákaznické podpory e-shopu, stejný tvar
  supportHours: SHOP.contact.supportHours,
};

// ---------------------------------------------------------------------------
// Přepínač telefonů (zadání Tomáše 2026-09-09): telefonní čísla jdou vypnout
// na CELÉM webu jedním místem, bez procházení kódu. Dvě páky, obě čte
// `phonesOn()`:
//   1. admin blok HTML kódů `<span id="bs-config" hidden data-bs-phones="off">`
//      (vzor `#bs-sale` v shoptet-includes/common-header.html)
//      — přepne to redaktor bez deploye;
//   2. `contact.showPhones` v manifestu shopu (shop, který telefon nemá vůbec).
// Každé místo, které telefon kreslí, se ptá `phonesOn()` a bez telefonu buď
// prvek vynechá (řádek „Spěchá to? …", pilulka v kontaktu), nebo ho nahradí
// e-mailem (karta podpory v draweru, kontakt v modalu servisu, help v hlavičce
// pokladny) — nikdy nezůstane sirotek jako „Spěchá to?" bez čísla. Pojistka
// pro nativní odkazy platformy: `html.bs-no-phone a[href^="tel:"]` v _base.scss.
// Hodnota žije v manifestu shopu (`contact.showPhones`); ⚠️ od 2026-09-10 je
// VYPNUTO — dokud je `false`, admin blok nic nezapne.
export const SHOW_PHONES = SHOP.contact.showPhones;

export function phonesOn() {
  if (!SHOW_PHONES) return false;
  const cfg = typeof document !== 'undefined' ? document.getElementById('bs-config') : null;
  return !(cfg && cfg.getAttribute('data-bs-phones') === 'off');
}

// `tel:` odkaz z čísla v zápisu pro čtení („+420 417 823 111" → bez mezer)
export function telHref(phone) {
  return 'tel:' + String(phone || '').replace(/\s/g, '');
}

// Jeden kontaktní slot „napište, jinak zavolejte" (help v hlavičce pokladny,
// karta podpory v klientské sekci a v draweru, kontakt v modalu servisu).
// Pořadí kanálů (Tomáš 2026-09-30, telefony co nejméně): chat → e-mail →
// telefon. Se zapnutým chatem (supportChat.js) je slot vstup do chatu
// (`chat: true`, volající ho předá `asChatTrigger`, `href` je záložní e-mail)
// a e-mail ukazuje volající vedle. Bez chatu platí přepínač telefonů
// (`phonesOn`, od 2026-09-10 vypnuto): číslo, jinak e-mail. Dřív tahle větev
// žila v pěti kopiích.
export function reachContact() {
  const chat = chatOn();
  const phone = !chat && phonesOn();
  return {
    chat: chat,
    phone: phone,
    href: phone ? telHref(CONTACT.phone) : 'mailto:' + CONTACT.email,
    text: chat ? LC.cta : phone ? CONTACT.phone : CONTACT.email,
    icon: chat ? 'chat' : phone ? 'phone' : 'mail',
  };
}

// Křestní jméno z celého jména (zadání Tomáše 2026-09-10: u konkrétních osob
// se na webu ukazuje jen křestní jméno). Konstanty v šabloně už křestní jsou —
// tohle je pro jména, která přicházejí z DAT administrace (autor článku), kde
// zkracovat musí šablona. Bere první slovo; „Redakce Jipos" a jiné dvouslovné
// popisy by zkrácení pokazilo, proto se helper volá jen tam, kde je hodnota
// opravdu jméno osoby.
export function firstName(name) {
  const s = String(name || '').trim();
  if (!s) return s;
  return s.split(/\s+/)[0];
}

// třída na <html> pro CSS větve (advisor na mobilu bez kolečka telefonu apod.)
export function markPhones() {
  document.documentElement.classList.toggle('bs-no-phone', !phonesOn());
}

// Akce s `tel:` odkazem (poradna kategorie, 404, hledání, pás servisu): bez
// telefonu se buď nahradí `alt` (jiný href/label), nebo vypadne (null).
export function phoneAction(a) {
  if (!a || !/^tel:/i.test(a.href || '') || phonesOn()) return a;
  if (!a.alt) return null;
  const out = Object.assign({}, a, a.alt);
  delete out.alt;
  return out;
}

// Okno daného dne, nebo null (zavřeno). Bez `schedule` platí prodejna.
export function openWindow(day, schedule) {
  return (schedule || CONTACT.openHours)[day] || null;
}

const DAY_SHORT = L.dayShort;
// týden v českém pořadí (pondělí první) — kvůli slučování „Po–Pá" / „So–Ne"
const WEEK = [1, 2, 3, 4, 5, 6, 0];

function hourText(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return hh + ':' + (mm < 10 ? '0' : '') + mm;
}

/**
 * Otevírací doba po řádcích, sousední dny se STEJNÝMI hodinami sloučené:
 * `[{label:'Po–Pá', value:'7:30–17:00'}, {label:'So–Ne', value:'8:30–13:00'}]`
 * (zavřený den by dostal řádek `value:'Zavřeno'`, `open:false`).
 *
 * Bydlí tady, ne v `shippingModel.js`, protože zdrojem je `CONTACT` a čte ho
 * i patička, 404 a klientská sekce — model dopravy ho jen reexportuje.
 *
 * @param {Date} [now] označí řádek s dneškem (`today`)
 * @param {object} [schedule] rozpis dnů; bez něj prodejna (`CONTACT.openHours`),
 *   pro podporu `CONTACT.supportHours` (nebo `supportHours()`)
 * @returns {{label: string, value: string, open: boolean, today: boolean}[]}
 */
export function openingHours(now, schedule) {
  const today = (now || new Date()).getDay();
  const runs = [];
  for (const day of WEEK) {
    const win = openWindow(day, schedule);
    const value = win ? hourText(win.from) + '–' + hourText(win.to) : L.closed;
    const last = runs[runs.length - 1];
    if (last && last.value === value) last.days.push(day);
    else runs.push({ value: value, open: !!win, days: [day] });
  }
  return runs.map(function (run) {
    const from = DAY_SHORT[run.days[0]];
    const to = DAY_SHORT[run.days[run.days.length - 1]];
    return {
      label: run.days.length > 1 ? from + '–' + to : from,
      value: run.value,
      open: run.open,
      today: run.days.indexOf(today) !== -1,
    };
  });
}

// Jednořádkový zápis: prodejna „Po–Pá 7:30–17:00 · So–Ne 8:30–13:00", podpora
// (`supportHoursText()`) „Po–Čt 8:00–15:00 · Pá 7:30–14:30". Zavřené dny se
// nevypisují (plynou z výčtu). `keep` = skupina dnů se nezalomí (pevné mezery,
// za pomlčkou word joiner), řádek se láme jen mezi skupinami. Podpora ho má od
// 2026-10-11, kdy dostala druhou skupinu (pátek) a na mobilu i v patičce se
// lámala za pomlčkou („Pá 7:30–" / „14:30").
export function hoursText(schedule, keep) {
  return openingHours(undefined, schedule)
    .filter(function (row) {
      return row.open;
    })
    .map(function (row) {
      const text = row.label + ' ' + row.value;
      return keep ? text.replace(/ /g, '\u00A0').replace(/–/g, '–\u2060') : text;
    })
    .join(' · ');
}

// Pracovní doba podpory po řádcích (víkend jako `open:false`).
export function supportHours(now) {
  return openingHours(now, CONTACT.supportHours);
}

export function supportHoursText() {
  return hoursText(CONTACT.supportHours, true);
}

CONTACT.hours = supportHoursText();
CONTACT.storeHours = hoursText();

export const ACCOUNT = { href: page('login'), label: L.login, icon: 'user' };
export const ACCOUNT_SIGNED = { href: page('account'), label: L.account, icon: 'user' };

// Přihlášený stav: dataLayer `customer.registered` (inline v <head>, v blank
// módu funguje — sonda 2026-08-31: anon `false`, přihlášený `true` + fullName);
// fallback nativní odkaz na odhlášení v DOM. Statický ACCOUNT posílal
// přihlášeného zákazníka na přihlašovací formulář (#223).
export function isSignedIn() {
  const c = shoptetData('customer');
  if (c && typeof c.registered === 'boolean') return c.registered;
  return !!document.querySelector('a[href*="/logout"]');
}

export function accountLink() {
  return isSignedIn() ? ACCOUNT_SIGNED : ACCOUNT;
}

// Aktivní velkoobchodní ceník u ikony účtu (mock schválený 2026-09-14, tichá
// varianta bez barevných chipů): desktop nese vedle ikony dva řádky „Můj účet /
// Jipos Premium", mobil (kde se text nevejde) tmavý mikroštítek „VO" vpravo
// dole — nahoře by se pletl s počtem položek u košíku. Zdroj = dataLayer,
// žádný dotaz navíc. Mimo VO zákazníka `null`.
export function accountWholesale() {
  return wholesaleCustomer(shoptetData('customer'));
}

/** Označí odkaz účtu stavem VO ceníku (hlavička); idempotentní. */
function markAccountWholesale(a, ctx) {
  if (a.querySelector('.bs-header-icon__vo')) return;
  a.classList.add('bs-header-icon--vo');
  a.setAttribute('aria-label', accountLabel(ctx));
  const txt = document.createElement('span');
  txt.className = 'bs-header-icon__txt';
  txt.setAttribute('aria-hidden', 'true');
  const eyebrow = document.createElement('span');
  eyebrow.className = 'bs-header-icon__eyebrow';
  eyebrow.textContent = VO.account;
  const level = document.createElement('span');
  level.className = 'bs-header-icon__level';
  level.textContent = ctx.name || VO.pricelistGeneric;
  txt.appendChild(eyebrow);
  txt.appendChild(level);
  a.appendChild(txt);
  const tag = document.createElement('span');
  tag.className = 'bs-header-icon__vo';
  tag.setAttribute('aria-hidden', 'true');
  tag.textContent = VO.tag;
  a.appendChild(tag);
}
// Oblíbené se v Shoptetu teprve zapínají (issue #35) — dokud nejsou, srdíčko
// nevykreslujeme, ať v headeru nesvítí odkaz do 404.
export const WISHLIST = { href: '/oblibene/', label: L.wishlist, icon: 'heart', enabled: false };
// Telefon je jen mobilní (desktop má kontaktní blok s e-mailem) — CSS ho nad 768 skryje.
const PHONE = {
  href: telHref(CONTACT.phone),
  label: L.call(CONTACT.phone),
  icon: 'phone',
  modifier: 'phone',
};

// Otevřeno teď? Bez `schedule` prodejna (víkend končí ve 13:00, viz
// CONTACT.openHours); podpora má vlastní `isSupportOnline`.
export function isOpenNow(now, schedule) {
  const d = now || new Date();
  const win = openWindow(d.getDay(), schedule);
  if (!win) return false;
  const h = d.getHours() + d.getMinutes() / 60;
  return h >= win.from && h < win.to;
}

// Podpora právě pracuje? Zelená tečka u telefonu a stav karty podpory v draweru.
// Svátky ČR podpora nepracuje, stejně jako sklad neexpeduje (dispatchEta.isHoliday,
// Tomáš 2026-10-12: „je to stejná logika").
export function isSupportOnline(now) {
  const d = now || new Date();
  return !isHoliday(d) && isOpenNow(d, CONTACT.supportHours);
}

/**
 * Stav podpory pro kontakt v hlavičce: v pracovní době konec dnešního okna,
 * mimo ni nejbližší začátek (`inDays` 0 = dnes, 1 = zítra, jinak `day` 0–6).
 * Svátek ČR je den bez podpory (`isHoliday`), stejně jako u `isSupportOnline`.
 * @returns {{online: true, until: string} | {online: false, inDays: number, day: number, from: string} | {online: false, inDays: null}}
 */
export function supportStatus(now) {
  const d = now || new Date();
  const h = d.getHours() + d.getMinutes() / 60;
  for (let i = 0; i < 15; i += 1) {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i, 12);
    const day = date.getDay();
    const win = isHoliday(date) ? null : openWindow(day, CONTACT.supportHours);
    if (!win) continue;
    if (i === 0 && h >= win.to) continue;
    if (i === 0 && h >= win.from) return { online: true, until: hourText(win.to) };
    return { online: false, inDays: i, day: day, from: hourText(win.from) };
  }
  return { online: false, inDays: null };
}

// „dnes" / „zítra" / „v pondělí" k `supportStatus()` mimo pracovní dobu
function supportWhen(s) {
  if (s.inDays === 0) return L.today;
  if (s.inDays === 1) return L.tomorrow;
  return L.onDay[s.day];
}

/** Krátký řádek pod „Zákaznická podpora": „Online do 15:00" / „Zpět v pondělí v 8:00". */
export function supportStateText(now) {
  const s = supportStatus(now);
  if (s.online) return L.supportUntil(s.until);
  return s.inDays === null ? '' : L.supportBack(supportWhen(s), s.from);
}

/** Totéž celou větou do panelu. */
export function supportStateLong(now) {
  const s = supportStatus(now);
  if (s.online) return L.supportOnlineLong(s.until);
  return s.inDays === null ? '' : L.supportOfflineLong(supportWhen(s), s.from);
}

// Kontakt v hlavičce (plátno docs/design/2026-10-12-hlavicka-kontakt/, varianta B,
// Tomáš 2026-10-12): fotka poradkyně s tečkou stavu podpory (zelená v pracovní
// době, červená mimo ni), „Zákaznická podpora" a krátký stav. Celý blok je vstup
// do chatu (bez chatu e-mail). Pracovní dobu a e-mail nese panel po najetí myší
// nebo fokusu, do 2026-10-12 stály v jednom nezalomitelném řádku, který s pátkem
// podpory narostl na 331 px a bral šířku poli hledání.
function face(size) {
  const wrap = document.createElement('span');
  wrap.className = 'bs-header-contact__face';
  const photo = document.createElement('span');
  photo.className = 'bs-header-contact__photo';
  fillAvatar(photo, 'header', size);
  wrap.appendChild(photo);
  const dot = document.createElement('span');
  dot.className = 'bs-header-contact__dot';
  dot.setAttribute('aria-hidden', 'true');
  wrap.appendChild(dot);
  return wrap;
}

function textEl(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

// Stav podpory do bloku i panelu. Zapisuje jen změnu (textContent v hlavičce
// je pro observery košíku mutace), volá se při stavbě, najetí, fokusu
// a návratu na kartu: stránka otevřená přes 15:00 se tak srovná sama.
function paintStatus(box) {
  const now = new Date();
  const online = isSupportOnline(now);
  box.classList.toggle('is-online', online);
  box.classList.toggle('is-offline', !online);
  const set = function (sel, text) {
    const el = box.querySelector(sel);
    if (el && el.textContent !== text) el.textContent = text;
  };
  set('.bs-header-contact__state', supportStateText(now));
  set('.bs-header-contact__status', supportStateLong(now));
  const rows = supportHours(now);
  box.querySelectorAll('.bs-header-contact__day').forEach(function (li, i) {
    li.classList.toggle('is-today', !!(rows[i] && rows[i].today));
  });
}

// Zkopíruje e-mail; bez Clipboard API (http, starý prohlížeč) přes výběr textu.
function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(
      function () {
        return true;
      },
      function () {
        return false;
      },
    );
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  area.remove();
  return Promise.resolve(ok);
}

function buildPanel(chat) {
  const panel = textEl('div', 'bs-header-contact__panel');
  const card = textEl('div', 'bs-header-contact__card');
  panel.appendChild(card);

  const head = textEl('div', 'bs-header-contact__head');
  head.appendChild(face(40));
  const titles = textEl('div', 'bs-header-contact__titles');
  titles.appendChild(textEl('span', 'bs-header-contact__name', HOURS.support));
  titles.appendChild(textEl('span', 'bs-header-contact__status'));
  head.appendChild(titles);
  card.appendChild(head);

  // rozpis po řádcích, sousední dny se stejnými hodinami sloučené; dny bez
  // podpory „–" (Tomáš 2026-10-12), dnešek tučně (paintStatus)
  const list = textEl('ul', 'bs-header-contact__days');
  for (const row of supportHours()) {
    const li = textEl('li', 'bs-header-contact__day');
    li.appendChild(textEl('span', '', row.label));
    li.appendChild(textEl('span', '', row.open ? row.value : L.supportClosed));
    list.appendChild(li);
  }
  card.appendChild(list);

  if (chat) {
    const btn = textEl('a', 'bs-header-contact__panel-chat');
    btn.href = 'mailto:' + CONTACT.email;
    btn.appendChild(icon('chat'));
    btn.appendChild(textEl('span', '', LC.cta));
    asChatTrigger(btn, 'header-panel');
    card.appendChild(btn);
  }

  const mailBox = textEl('div', 'bs-header-contact__mailbox');
  mailBox.appendChild(textEl('span', 'bs-header-contact__mail-lead', chat ? L.mailLead : L.mailLeadOnly));
  const mailRow = textEl('div', 'bs-header-contact__mail-row');
  const mail = textEl('a', 'bs-header-contact__mail');
  mail.href = 'mailto:' + CONTACT.email;
  mail.appendChild(icon('mail'));
  mail.appendChild(textEl('span', '', CONTACT.email));
  mailRow.appendChild(mail);
  const copy = textEl('button', 'bs-header-contact__copy');
  copy.type = 'button';
  copy.appendChild(icon('copy', 'bs-header-contact__copy-idle'));
  copy.appendChild(icon('check', 'bs-header-contact__copy-done'));
  const copyLabel = textEl('span', '', L.copy);
  copyLabel.setAttribute('aria-live', 'polite');
  copy.appendChild(copyLabel);
  let reset = 0;
  copy.addEventListener('click', function () {
    copyText(CONTACT.email).then(function (ok) {
      if (!ok) {
        // bez schránky aspoň označit adresu pro Ctrl+C
        const range = document.createRange();
        range.selectNodeContents(mail.lastChild);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      copy.classList.add('is-copied');
      copyLabel.textContent = L.copied;
      clearTimeout(reset);
      reset = setTimeout(function () {
        copy.classList.remove('is-copied');
        copyLabel.textContent = L.copy;
      }, 2000);
    });
  });
  mailRow.appendChild(copy);
  mailBox.appendChild(mailRow);
  card.appendChild(mailBox);

  return panel;
}

function buildContact() {
  const chat = chatOn();
  const box = document.createElement('div');
  box.className = 'bs-header-contact';

  const lead = textEl('a', 'bs-header-contact__lead');
  lead.href = 'mailto:' + CONTACT.email;
  if (chat) asChatTrigger(lead, 'header');
  lead.appendChild(face(46));
  const text = textEl('span', 'bs-header-contact__text');
  text.appendChild(textEl('span', 'bs-header-contact__title', HOURS.support));
  text.appendChild(textEl('span', 'bs-header-contact__state'));
  lead.appendChild(text);
  box.appendChild(lead);
  box.appendChild(buildPanel(chat));
  paintStatus(box);

  // Panel ukazuje CSS (najetí s prodlevou, fokus klávesnicí). Esc ho zavře
  // i pod kurzorem (WCAG 1.4.13), klik na vstup taky, ať nevisí přes okno chatu;
  // zpět se ukáže po odjetí a novém najetí nebo odchodu fokusu.
  const reopen = function () {
    box.classList.remove('is-dismissed');
  };
  box.addEventListener('mouseenter', function () {
    paintStatus(box);
  });
  box.addEventListener('focusin', function () {
    paintStatus(box);
  });
  box.addEventListener('mouseleave', reopen);
  box.addEventListener('focusout', function (e) {
    if (!box.contains(e.relatedTarget)) reopen();
  });
  lead.addEventListener('click', function () {
    box.classList.add('is-dismissed');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && (box.matches(':hover') || box.contains(document.activeElement))) {
      box.classList.add('is-dismissed');
    }
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') paintStatus(box);
  });

  return box;
}

function buildIcons() {
  const wrap = document.createElement('div');
  wrap.className = 'bs-header-icons';
  const items = [PHONE, WISHLIST, accountLink()].filter(function (i) {
    if (i === PHONE && !phonesOn()) return false;
    return i.enabled !== false;
  });
  for (const item of items) {
    const a = document.createElement('a');
    a.className = 'bs-header-icon' + (item.modifier ? ' bs-header-icon--' + item.modifier : '');
    a.href = item.href;
    a.setAttribute('aria-label', item.label);
    a.setAttribute('rel', 'nofollow');
    a.appendChild(icon(item.icon));
    const vo = item === ACCOUNT_SIGNED ? accountWholesale() : null;
    if (vo) markAccountWholesale(a, vo);
    if (item === PHONE && isSupportOnline()) {
      const dot = document.createElement('span');
      dot.className = 'bs-header-icon__dot';
      dot.setAttribute('aria-hidden', 'true');
      a.appendChild(dot);
    }
    wrap.appendChild(a);
  }
  return wrap;
}

export function initHeaderTools() {
  const wrapper = document.querySelector('#header .header-top-wrapper')
    || document.querySelector('#header .header-top .container');
  if (!wrapper) return;

  // lupa do nativního (prázdného) spanu v search fieldsetu
  const searchIcon = wrapper.querySelector('.search-input-icon');
  if (searchIcon && !searchIcon.querySelector('svg')) searchIcon.appendChild(icon('search'));

  const buttons = wrapper.querySelector('.navigation-buttons');

  if (!wrapper.querySelector('.bs-header-contact')) {
    const contact = buildContact();
    if (buttons) wrapper.insertBefore(contact, buttons);
    else wrapper.appendChild(contact);
  }

  if (!wrapper.querySelector('.bs-header-icons')) {
    const icons = buildIcons();
    if (icons.children.length) {
      if (buttons) wrapper.insertBefore(icons, buttons);
      else wrapper.appendChild(icons);
    }
  }

  // červené kolečko s ikonou košíku uvnitř nativní pilulky (.cart-count).
  // Text pilulky plní Shoptet (.cart-price) + cartCounter.js.
  const cart = wrapper.querySelector('a.cart-count');
  if (cart && !cart.querySelector('.bs-cart-icon')) {
    const circle = document.createElement('span');
    circle.className = 'bs-cart-icon';
    circle.appendChild(icon('cart'));
    cart.insertBefore(circle, cart.firstChild);
  }
}
